import React, { useRef } from 'react';
import '../../css/chats/Waveform.css';

/**
 * Переиспользуемый компонент визуализации waveform.
 *
 * Отображает массив амплитуд как вертикальные столбики.
 * Поддерживает:
 * - progress (0..1) — позиция воспроизведения, столбики до неё подсвечены
 * - onSeek(fraction) — клик по waveform для перемотки
 * - liveAmplitudes — живой массив для режима записи (без progress)
 *
 * @param {number[]} [amplitudes] — массив амплитуд 0..1 (для воспроизведения)
 * @param {number[]} [liveAmplitudes] — живой массив для записи (альтернатива amplitudes)
 * @param {number} [progress=0] — позиция воспроизведения 0..1
 * @param {Function} [onSeek] — колбэк перемотки: (fraction: number) => void
 * @param {string} [className] — доп. CSS-класс
 */
export default function Waveform({
    amplitudes,
    liveAmplitudes,
    progress = 0,
    onSeek,
    className = '',
}) {
    const containerRef = useRef(null);

    const bars = liveAmplitudes || amplitudes || [];

    // Для воспроизведения: вычисляем количество подсвеченных столбиков
    const progressIndex = Math.floor(progress * bars.length);

    const handleClick = (e) => {
        if (!onSeek || !containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const fraction = Math.max(0, Math.min(1, x / rect.width));
        onSeek(fraction);
    };

    // Стиль для live-режима: столбики добавляются динамически
    const isLive = !!liveAmplitudes;

    return (
        <div
            ref={containerRef}
            className={`waveform ${isLive ? 'waveform--live' : ''} ${onSeek ? 'waveform--seekable' : ''} ${className}`}
            onClick={handleClick}
            role={onSeek ? 'slider' : undefined}
            aria-label={onSeek ? 'Прогресс воспроизведения' : undefined}
        >
            {bars.length === 0 ? (
                // Плейсхолдер: плоские столбики
                Array.from({ length: 40 }).map((_, i) => (
                    <div
                        key={i}
                        className="waveform__bar waveform__bar--placeholder"
                    />
                ))
            ) : (
                bars.map((amp, i) => (
                    <div
                        key={i}
                        className={`waveform__bar ${i < progressIndex ? 'waveform__bar--active' : ''}`}
                        style={{ height: `${Math.max(8, amp * 100)}%` }}
                    />
                ))
            )}
        </div>
    );
}
